import { h, Fragment } from 'preact';
import { useState, useEffect } from 'preact/hooks';
import { formatMoney } from 'utils/storefront/format-resource';
import Image from 'utils/image';
import CartService from '../cart/cart-service';

const ProductStyleItWith = ({ cart, ...props }) => {
  const [cartState, setCartState] = useState(CartService.cart);
  const [loading, setLoading] = useState(CartService.loading);
  const [preventedPurchaseItem, setPreventedPurchaseItem] = useState();
  const [styleItWithItems, setStyleItWithItems] =
    useState(props.product.variants.find(
      (v) =>
        JSON.stringify(v.selectedOptions) ===
        JSON.stringify(props.product.variantBySelectedOptions.selectedOptions),
    ).styleItWith);
  const [styleItWithItemsDisplay, setStyleItWithItemsDisplay] = useState();
  const { safetyStockQuantity } = window.TMW;
  const [ignoreSafetyStock, setIgnoreSafetyStock] = useState(
    props.product.ignoreSafetyStock
  );
  const [cardIndexClicked, setCardIndexClicked] = useState();

  useEffect(() => {
    const listener = () => {
      setLoading(CartService.loading);
      setCartState(CartService.cart);
    }
    CartService.addListener(listener);
    setCartState(CartService.cart);
    return () => {
      CartService.removeListener(listener);
    }
  }, []);

  const handleCurrentVariantUpdated = (e) => {
    const { currentProductData } = e.detail;
    setStyleItWithItems(currentProductData.variants.find(
      (v) =>
        JSON.stringify(v.id) ===
        JSON.stringify(e.detail.currentVariant),
    ).styleItWith);
    setIgnoreSafetyStock(currentProductData.ignoreSafetyStock);
  };

  useEffect(() => {
    document.addEventListener("currentVariantUpdated", handleCurrentVariantUpdated);
    return () => document.removeEventListener('currentVariantUpdated', handleCurrentVariantUpdated);
  }, []);

  useEffect(() => {
    if (loading) return;
    let filteredStyleItWith =
      styleItWithItems?.filter((styleItWithItem) =>
        !cartState?.items?.filter((stateItem) =>
          stateItem.id === styleItWithItem.id).length &&
        styleItWithItem.availableForSale) || [];

    if (filteredStyleItWith.length && !ignoreSafetyStock) {
      filteredStyleItWith = filteredStyleItWith.filter(
        (styleItWithItem) =>
          styleItWithItem.inventoryQuantity > safetyStockQuantity);
    }
    setStyleItWithItemsDisplay(filteredStyleItWith);
  }, [styleItWithItems, cartState, loading, ignoreSafetyStock]);

  const handleAddToCart = async (e, index) => {
    setCardIndexClicked(index);
    e.preventDefault();
    const id = e.currentTarget.getAttribute('data-variant-id');
    const variantToAdd = `/${id}`;

    try {
      await CartService.add({
        merchandiseId: variantToAdd,
        quantity: 1,
      });
      setPreventedPurchaseItem(null);
    } catch (error) {
      if (error.message === "Prevent purchase with other items") {
        setPreventedPurchaseItem(error.data.item);
      }
      if (error.message === "Out of stock") {
        const filteredbySafetyStock = styleItWithItemsDisplay.filter(
          (item) => item.id !== Number(id));
        setStyleItWithItemsDisplay(filteredbySafetyStock);
      }
    }
  };

  return (
    <>
      {styleItWithItemsDisplay?.length > 0 && (
        <div className="flex flex-col pr-[18px] pt-7 gap-y-[14px] lg:px-0 lg:pt-8 lg:gap-y-[10px]">
          <div className="uppercase utility-1">
            {window.TMW.Strings.product.style_it_with_title}
          </div>
          {styleItWithItemsDisplay.map((item, index) => (
            <div
              className='relative flex flex-col gap-1 md:gap-2 border-primary lg:border lg:pt-[15px] lg:px-[11px] lg:pb-3'
              key={item.id}
            >
              <div className="flex gap-4 lg:gap-7">
                <a className="no-underline paragraph-2"
                  href={item.url}
                >
                  <Image
                    classes="h-auto w-[107px] min-w-[107px] aspect-square lg:w-[82px] lg:min-w-auto"
                    src={item.image.src}
                    srcWidths={{ sm: 200, md: 400, lg: 680 }}
                    width={item.image.width}
                    height={item.image.height}
                    alt={item.image.altText || item.productName}
                  />
                </a>
                <div className="flex flex-col gap-y-[11px] justify-center lg:gap-y-[14px] lg:gap-x-7 lg:flex-row lg:items-center flex-grow">
                  <div className="flex flex-col gap-1.5 lg:flex-grow">
                    <a className="no-underline no-underitem text-primary paragraph-2"
                      href={item.url}>
                      {item.productName}
                    </a>
                    <div className="paragraph-2 lg:paragraph-3">
                      {item.compareAtPrice?.amount > 0 && (
                        <s className='pr-1 text-grey-5'>
                          {formatMoney({
                            amount: item.compareAtPrice.amount,
                            currencyCode: window.TMW?.localization?.currencyCode || 'USD',
                          })}
                        </s>
                      )}
                      {formatMoney({
                        amount: item.price.amount,
                        currencyCode: window.TMW?.localization?.currencyCode || 'USD',
                      })}
                    </div>
                  </div>
                  <div>
                    <a
                      href="#"
                      type="button"
                      className="cta whitespace-nowrap text-14 lg:mr-[23px] lg:min-w-[90px]"
                      onClick={(e) => handleAddToCart(e, index)}
                      data-variant-id={item.id}>
                      {window.TMW.Strings.product.addToCart}
                    </a>
                  </div>
                </div>
              </div>
              {preventedPurchaseItem && cardIndexClicked === index && (
                <div className="flex items-baseline">
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-red-500 mr-2 mt-0.25 flex-shrink-0 flex-grow-0" />
                  <span className="font-light text-red-500 paragraph-5">
                    {window.TMW.Strings.product.preventPurchase.replace(
                      "{{ product_title }}", `${preventedPurchaseItem?.product?.title} - ${preventedPurchaseItem?.selectedOptions[0]?.value}`
                    )}
                  </span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </>
  );
};

export default ProductStyleItWith;
